import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Badge,
} from '@mui/material';
import {
  Person as PersonIcon,
  Work as WorkIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Assessment as AssessmentIcon,
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Group as GroupIcon,
  Timeline as TimelineIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import Layout from 'Component/Layouts/layout';
import rootAxios from 'requests/rootAxios';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import BlockIcon from '@mui/icons-material/Block';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import WorkOffIcon from '@mui/icons-material/WorkOff';

export default function EmployeeData() {
  const [employeeId, setEmployeeId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [employeeData, setEmployeeData] = useState(null);

  const handleInputChange = (event) => {
    const value = event.target.value;
    // Only allow numbers and max 8 digits
    if (/^\d{0,8}$/.test(value)) {
      setEmployeeId(value);
      setError('');
    }
  };

  const handleSearch = async () => {
    if (!employeeId || employeeId.length === 0) {
      setError('Please enter a valid employee ID');
      return;
    }

    setLoading(true);
    setError('');
    setEmployeeData(null);

    try {
      const response = await rootAxios.post('/user/get-emp-wise-data', {
        empid: parseInt(employeeId),
      });

      if (response.data.status === 'success') {
        setEmployeeData(response.data.data);
      } else {
        setError('Failed to fetch employee data');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching employee data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'assigned':
        return 'warning';
      case 'pending':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon />;
      case 'assigned':
        return <PendingIcon />;
      case 'pending':
        return <PendingIcon />;
      default:
        return <CancelIcon />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return timeString;
  };

  return (
    <Layout>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          p: { xs: 2, md: 3 },
        }}
      >
        {/* Professional Header */}
        <Box
          sx={{
            mb: 2,
            background: '#ffffff',
            borderRadius: '8px',
            p: 2,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0',
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <Box>
              <Typography
                variant="h5"
                fontWeight="600"
                sx={{
                  color: '#1e293b',
                  mb: 0.25,
                }}
              >
                Employee Data Lookup
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontWeight: '400' }}>
                Search and view comprehensive employee training data and performance metrics
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Search Section */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e2e8f0',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            mb: 3,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: '700',
                marginBottom: '16px',
                color: '#1e293b',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <PersonIcon sx={{ color: '#3b82f6' }} />
              Search Employee
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{ flexWrap: 'wrap', gap: 2 }}
            >
              <TextField
                label="Employee ID"
                value={employeeId}
                onChange={handleInputChange}
                placeholder="Enter employee ID"
                variant="outlined"
                inputProps={{ maxLength: 8 }}
                sx={{
                  minWidth: { xs: '100%', sm: 280 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '& fieldset': {
                      borderColor: '#e2e8f0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#3b82f6',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3b82f6',
                      borderWidth: 2,
                    },
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading || !employeeId}
                startIcon={loading ? <CircularProgress size={20} /> : <PersonIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  borderRadius: '12px',
                  px: 3,
                  py: 1.5,
                  fontWeight: '600',
                  textTransform: 'none',
                  boxShadow: '0 4px 14px 0 rgba(59, 130, 246, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px 0 rgba(59, 130, 246, 0.4)',
                  },
                  '&:disabled': {
                    background: '#e2e8f0',
                    color: '#94a3b8',
                    transform: 'none',
                    boxShadow: 'none',
                  },
                }}
              >
                {loading ? 'Searching...' : 'Search Employee'}
              </Button>
            </Stack>
            {error && (
              <Alert
                severity="error"
                sx={{
                  marginTop: 2,
                  borderRadius: '12px',
                  border: '1px solid #fecaca',
                  backgroundColor: '#fef2f2',
                }}
              >
                {error}
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Employee Data Dashboard */}
        {employeeData && (
          <Box>
            {/* Employee Profile Header */}
            <Card elevation={3} sx={{ marginBottom: '24px', borderRadius: '12px' }}>
              <CardContent sx={{ padding: '24px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 3 }}>
                  <Avatar
                    sx={{
                      marginRight: 3,
                      backgroundColor: 'primary.main',
                      width: 64,
                      height: 64,
                      fontSize: '1.5rem',
                    }}
                  >
                    {employeeData.employee.basicInfo.name
                      .split(' ')
                      .map((n) => n[0])
                      .join('')}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h4"
                      component="h2"
                      sx={{ fontWeight: '600', marginBottom: 1 }}
                    >
                      {employeeData.employee.basicInfo.name}
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ marginBottom: 1 }}>
                      Employee ID: {employeeData.employee.basicInfo.employeeId}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Chip
                        label={employeeData.employee.basicInfo.role}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                      <Chip
                        label={employeeData.employee.basicInfo.designation}
                        color="secondary"
                        variant="outlined"
                        size="small"
                      />
                      <Chip
                        label={employeeData.employee.basicInfo.department}
                        color="info"
                        variant="outlined"
                        size="small"
                      />
                      {employeeData.employee.basicInfo.employmentStatus && (
                        <Chip
                          icon={
                            employeeData.employee.basicInfo.employmentStatus === 'Active' ? (
                              <CheckCircleIcon />
                            ) : (
                              <WorkOffIcon />
                            )
                          }
                          label={employeeData.employee.basicInfo.employmentStatus}
                          color={
                            employeeData.employee.basicInfo.employmentStatus === 'Active'
                              ? 'success'
                              : 'error'
                          }
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        padding: 2,
                        backgroundColor: 'grey.50',
                        borderRadius: 2,
                      }}
                    >
                      <LocationIcon sx={{ fontSize: 32, color: 'primary.main', marginBottom: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: '600' }}>
                        {employeeData.employee.basicInfo.region}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Region
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        padding: 2,
                        backgroundColor: 'grey.50',
                        borderRadius: 2,
                      }}
                    >
                      <BusinessIcon
                        sx={{ fontSize: 32, color: 'secondary.main', marginBottom: 1 }}
                      />
                      <Typography variant="h6" sx={{ fontWeight: '600' }}>
                        {employeeData.employee.basicInfo.branch}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Branch
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        padding: 2,
                        backgroundColor: 'grey.50',
                        borderRadius: 2,
                      }}
                    >
                      <CalendarIcon sx={{ fontSize: 32, color: 'success.main', marginBottom: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: '600' }}>
                        {employeeData.employee.basicInfo.dateOfJoining}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Date of Joining
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        padding: 2,
                        backgroundColor: 'grey.50',
                        borderRadius: 2,
                      }}
                    >
                      <GroupIcon sx={{ fontSize: 32, color: 'warning.main', marginBottom: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: '600' }}>
                        {employeeData.employee.basicInfo.gender}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Gender
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Retain Date */}
                {employeeData.employee.basicInfo.retainDate && (
                  <Box sx={{ marginTop: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        padding: 2,
                        backgroundColor: '#f0fdf4',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'success.light',
                      }}
                    >
                      <EventAvailableIcon sx={{ color: 'success.main', fontSize: 24 }} />
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, color: 'success.dark' }}
                        >
                          Retain Date
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'success.dark' }}>
                          {formatDate(employeeData.employee.basicInfo.retainDate)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Rejoined Information */}
                {employeeData.employee.basicInfo.rejoinedApiResponse &&
                  employeeData.employee.basicInfo.rejoinedApiResponse.length > 0 && (
                    <Box sx={{ marginTop: 3 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: '600',
                          marginBottom: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <PersonIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                        Rejoined Information
                      </Typography>
                      <Box
                        sx={{
                          padding: 2,
                          backgroundColor: 'warning.50',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'warning.light',
                        }}
                      >
                        <List disablePadding>
                          {employeeData.employee.basicInfo.rejoinedApiResponse.map(
                            (item, index) => (
                              <ListItem
                                key={index}
                                sx={{ px: 0, flexDirection: 'column', alignItems: 'flex-start' }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    marginBottom: 0.5,
                                  }}
                                >
                                  <Chip
                                    label={
                                      item.isRejoined === 'true' ? 'Rejoined' : item.isRejoined
                                    }
                                    color="warning"
                                    size="small"
                                  />
                                  <Typography variant="body2" color="text.secondary">
                                    Employee ID: {item.employeeId}
                                  </Typography>
                                </Box>
                                {item.comment && (
                                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                    {item.comment}
                                  </Typography>
                                )}
                              </ListItem>
                            )
                          )}
                        </List>
                      </Box>
                    </Box>
                  )}
              </CardContent>
            </Card>

            {/* Training Bypasses */}
            {employeeData.bypasses && employeeData.bypasses.length > 0 && (
              <Card
                elevation={3}
                sx={{
                  marginBottom: '24px',
                  borderRadius: '12px',
                  border: '1px solid #fca5a5',
                  backgroundColor: '#fff7f7',
                }}
              >
                <CardContent sx={{ padding: '24px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                    <BlockIcon sx={{ marginRight: 1, color: 'error.main', fontSize: 28 }} />
                    <Typography variant="h6" sx={{ fontWeight: '700', color: '#991b1b' }}>
                      Training Bypasses ({employeeData.bypasses.length})
                    </Typography>
                  </Box>
                  <TableContainer
                    component={Paper}
                    sx={{ borderRadius: '8px', border: '1px solid #fecaca' }}
                  >
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: '#fef2f2' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: '700', color: '#991b1b' }}>
                            Training Type
                          </TableCell>
                          <TableCell sx={{ fontWeight: '700', color: '#991b1b' }}>Reason</TableCell>
                          <TableCell sx={{ fontWeight: '700', color: '#991b1b' }}>
                            Added By
                          </TableCell>
                          <TableCell sx={{ fontWeight: '700', color: '#991b1b' }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: '700', color: '#991b1b' }}>
                            Attachment
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {employeeData.bypasses.map((bypass, index) => (
                          <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#fef2f2' } }}>
                            <TableCell>
                              <Chip
                                label={bypass.trainingType.replace(/_/g, ' ')}
                                size="small"
                                sx={{
                                  backgroundColor: '#fecaca',
                                  color: '#991b1b',
                                  fontWeight: '600',
                                  border: '1px solid #f87171',
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{bypass.reason}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: '500' }}>
                                {bypass.addedByName || bypass.addedBy}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(bypass.createdAt)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {bypass.attachmentUrl ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  startIcon={<OpenInNewIcon />}
                                  href={bypass.attachmentUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ textTransform: 'none', borderRadius: '8px' }}
                                >
                                  View
                                </Button>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  N/A
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Training Summary Cards */}
            <Grid container spacing={3} sx={{ marginBottom: '24px' }}>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card elevation={2} sx={{ borderRadius: '12px', textAlign: 'center' }}>
                  <CardContent sx={{ padding: '20px' }}>
                    <SchoolIcon sx={{ fontSize: 40, color: 'primary.main', marginBottom: 1 }} />
                    <Typography variant="h4" sx={{ fontWeight: '700', color: 'primary.main' }}>
                      {employeeData.employee.summary.totalTrainingTypes}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Training Types
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card elevation={2} sx={{ borderRadius: '12px', textAlign: 'center' }}>
                  <CardContent sx={{ padding: '20px' }}>
                    <AssessmentIcon sx={{ fontSize: 40, color: 'info.main', marginBottom: 1 }} />
                    <Typography variant="h4" sx={{ fontWeight: '700', color: 'info.main' }}>
                      {employeeData.employee.summary.totalBatchesAssigned}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Batches Assigned
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card elevation={2} sx={{ borderRadius: '12px', textAlign: 'center' }}>
                  <CardContent sx={{ padding: '20px' }}>
                    <CheckCircleIcon
                      sx={{ fontSize: 40, color: 'success.main', marginBottom: 1 }}
                    />
                    <Typography variant="h4" sx={{ fontWeight: '700', color: 'success.main' }}>
                      {employeeData.employee.summary.completedBatches}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Completed
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card elevation={2} sx={{ borderRadius: '12px', textAlign: 'center' }}>
                  <CardContent sx={{ padding: '20px' }}>
                    <PendingIcon sx={{ fontSize: 40, color: 'warning.main', marginBottom: 1 }} />
                    <Typography variant="h4" sx={{ fontWeight: '700', color: 'warning.main' }}>
                      {employeeData.employee.summary.pendingBatches}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pending
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card elevation={2} sx={{ borderRadius: '12px', textAlign: 'center' }}>
                  <CardContent sx={{ padding: '20px' }}>
                    <PersonIcon sx={{ fontSize: 40, color: 'secondary.main', marginBottom: 1 }} />
                    <Typography variant="h4" sx={{ fontWeight: '700', color: 'secondary.main' }}>
                      {employeeData.employee.summary.totalNominations}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Nominations
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Hierarchy Information */}
            <Grid container spacing={3} sx={{ marginBottom: '24px' }}>
              {/* Region Manager Information */}
              {employeeData.employee.basicInfo.regionInfo && (
                <Grid item xs={12} md={6}>
                  <Card elevation={2} sx={{ borderRadius: '12px', height: '100%' }}>
                    <CardContent sx={{ padding: '20px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                        <SupervisorAccountIcon sx={{ marginRight: 1, color: 'primary.main' }} />
                        <Typography variant="h6" sx={{ fontWeight: '600' }}>
                          Region Manager
                        </Typography>
                      </Box>
                      <Box sx={{ padding: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: '600', marginBottom: 1 }}>
                          {employeeData.employee.basicInfo.regionInfo.regionManager.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1 }}>
                          ID: {employeeData.employee.basicInfo.regionInfo.regionManager.employeeId}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1 }}>
                          {employeeData.employee.basicInfo.regionInfo.regionManager.designation}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1 }}>
                          <EmailIcon
                            sx={{ fontSize: 16, marginRight: 0.5, verticalAlign: 'middle' }}
                          />
                          {employeeData.employee.basicInfo.regionInfo.regionManager.email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Region: {employeeData.employee.basicInfo.regionInfo.regionName} (
                          {employeeData.employee.basicInfo.regionInfo.regionId})
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Circle Head Information */}
              {employeeData.employee.basicInfo.circleHead && (
                <Grid item xs={12} md={6}>
                  <Card elevation={2} sx={{ borderRadius: '12px', height: '100%' }}>
                    <CardContent sx={{ padding: '20px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                        <BusinessIcon sx={{ marginRight: 1, color: 'secondary.main' }} />
                        <Typography variant="h6" sx={{ fontWeight: '600' }}>
                          Circle Head
                        </Typography>
                      </Box>
                      <Box sx={{ padding: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: '600', marginBottom: 1 }}>
                          {employeeData.employee.basicInfo.circleHead.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1 }}>
                          ID: {employeeData.employee.basicInfo.circleHead.employeeId}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1 }}>
                          {employeeData.employee.basicInfo.circleHead.designation}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1 }}>
                          <EmailIcon
                            sx={{ fontSize: 16, marginRight: 0.5, verticalAlign: 'middle' }}
                          />
                          {employeeData.employee.basicInfo.circleHead.email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Circle: {employeeData.employee.basicInfo.circleHead.circleName} (
                          {employeeData.employee.basicInfo.circleHead.circleId})
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>

            {/* Nominations Timeline */}
            {employeeData.employee.summary.allNominatedBatchIds &&
              employeeData.employee.summary.allNominatedBatchIds.length > 0 && (
                <Card elevation={2} sx={{ marginBottom: '24px', borderRadius: '12px' }}>
                  <CardContent sx={{ padding: '20px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                      <TimelineIcon sx={{ marginRight: 1, color: 'info.main' }} />
                      <Typography variant="h6" sx={{ fontWeight: '600' }}>
                        Nomination History
                      </Typography>
                    </Box>
                    <List>
                      {employeeData.employee.summary.allNominatedBatchIds.map(
                        (nomination, index) => (
                          <ListItem key={index} sx={{ paddingX: 0 }}>
                            <ListItemIcon>
                              <Badge badgeContent={index + 1} color="primary">
                                <SchoolIcon color="action" />
                              </Badge>
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box>
                                  <Typography variant="body1" sx={{ fontWeight: '600' }}>
                                    {nomination.batchId}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Training Type: {nomination.trainingType}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Typography variant="body2" color="text.secondary">
                                  Nominated on: {formatDate(nomination.nominatedAt)}
                                </Typography>
                              }
                            />
                          </ListItem>
                        )
                      )}
                    </List>
                  </CardContent>
                </Card>
              )}

            {/* Training Types and Batches */}
            {Object.entries(employeeData.trainingTypes).map(([trainingType, data]) => (
              <Accordion
                key={trainingType}
                elevation={2}
                sx={{ marginBottom: '16px', borderRadius: '12px' }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: 'grey.50',
                    borderRadius: '12px 12px 0 0',
                    '&.Mui-expanded': {
                      borderRadius: '12px 12px 0 0',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <SchoolIcon sx={{ marginRight: 2, color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: '600', flex: 1 }}>
                      {trainingType.replace(/_/g, ' ')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, marginRight: 2 }}>
                      <Chip
                        label={`${data.completedBatches} Completed`}
                        color="success"
                        size="small"
                        icon={<CheckCircleIcon />}
                      />
                      <Chip
                        label={`${data.pendingBatches} Pending`}
                        color="warning"
                        size="small"
                        icon={<PendingIcon />}
                      />
                      <Chip
                        label={`${data.nominationCount} Nominations`}
                        color="info"
                        size="small"
                        icon={<PersonIcon />}
                      />
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: '24px' }}>
                  {/* Batch Details Table */}
                  <TableContainer sx={{ marginBottom: 3 }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: '600' }}>Batch ID</TableCell>
                          <TableCell sx={{ fontWeight: '600' }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: '600' }}>Duration</TableCell>
                          <TableCell sx={{ fontWeight: '600' }}>Location</TableCell>
                          <TableCell sx={{ fontWeight: '600' }}>Assigned Date</TableCell>
                          <TableCell sx={{ fontWeight: '600' }}>Completion Date</TableCell>
                          <TableCell sx={{ fontWeight: '600' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: '600' }}>Completed</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.batchDetails.map((batch, index) => (
                          <TableRow key={index} hover>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontFamily: 'monospace', fontWeight: '500' }}
                              >
                                {batch.batchId}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={batch.batchType}
                                size="small"
                                variant="outlined"
                                color={batch.batchType === 'offline' ? 'primary' : 'secondary'}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: '500' }}>
                                {batch.duration}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}
                              >
                                {batch.location}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(batch.assignedDate)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(batch.completionDate)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={getStatusIcon(batch.status)}
                                label={batch.status}
                                color={getStatusColor(batch.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={
                                  batch.trainingCompleted ? <CheckCircleIcon /> : <CancelIcon />
                                }
                                label={batch.trainingCompleted ? 'Yes' : 'No'}
                                color={batch.trainingCompleted ? 'success' : 'error'}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Training Schedule */}
                  {data.batchDetails[0]?.dates && (
                    <Box sx={{ marginBottom: 3 }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ fontWeight: '600', marginBottom: 2 }}
                      >
                        <TimelineIcon sx={{ marginRight: 1, verticalAlign: 'middle' }} />
                        Training Schedule
                      </Typography>
                      <Grid container spacing={2}>
                        {data.batchDetails[0].dates.map((dateInfo, index) => (
                          <Grid item xs={12} sm={6} md={4} key={index}>
                            <Card elevation={1} sx={{ padding: 2, backgroundColor: 'grey.50' }}>
                              <Typography
                                variant="h6"
                                sx={{ fontWeight: '600', color: 'primary.main', marginBottom: 1 }}
                              >
                                {dateInfo.day}
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: '500', marginBottom: 0.5 }}
                              >
                                {formatDate(dateInfo.date)}
                              </Typography>
                              {data.batchDetails[0].datesEnd[index] && (
                                <Typography variant="body2" color="text.secondary">
                                  End Time: {formatTime(data.batchDetails[0].datesEnd[index].date)}
                                </Typography>
                              )}
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* Performance Statistics */}
                  {data.statistics && (
                    <Box>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ fontWeight: '600', marginBottom: 2 }}
                      >
                        <TrendingUpIcon sx={{ marginRight: 1, verticalAlign: 'middle' }} />
                        Performance Statistics
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                          <Card
                            elevation={1}
                            sx={{ padding: 2, textAlign: 'center', backgroundColor: 'primary.50' }}
                          >
                            <Typography
                              variant="h4"
                              color="primary.main"
                              sx={{ fontWeight: '700' }}
                            >
                              {data.statistics.averageAttendance}%
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Avg Attendance
                            </Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Card
                            elevation={1}
                            sx={{
                              padding: 2,
                              textAlign: 'center',
                              backgroundColor: 'secondary.50',
                            }}
                          >
                            <Typography
                              variant="h4"
                              color="secondary.main"
                              sx={{ fontWeight: '700' }}
                            >
                              {data.statistics.averagePreAssessment}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Pre Assessment
                            </Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Card
                            elevation={1}
                            sx={{ padding: 2, textAlign: 'center', backgroundColor: 'success.50' }}
                          >
                            <Typography
                              variant="h4"
                              color="success.main"
                              sx={{ fontWeight: '700' }}
                            >
                              {data.statistics.averagePostAssessment}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Post Assessment
                            </Typography>
                          </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Card
                            elevation={1}
                            sx={{ padding: 2, textAlign: 'center', backgroundColor: 'info.50' }}
                          >
                            <Typography variant="h4" color="info.main" sx={{ fontWeight: '700' }}>
                              {data.statistics.totalImprovement}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Improvement
                            </Typography>
                          </Card>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </Box>
    </Layout>
  );
}
